/**
 * Dubins Path Optimizer — Monte Carlo route generation for mission points.
 *
 * Instead of the corridor-detection algorithm in missionRouting.ts, this module
 * optimises per-point *direction* (heading) and *turn radius* using a Monte Carlo
 * approach whose cost function is total path length.  Because a straight line is
 * the shortest connection between two points, the optimizer naturally favours
 * corridors when points happen to be collinear.
 *
 * Each waypoint is modelled as:
 *   position  — fixed (lat/lng from pattern or user)
 *   direction — optimisable angle θ ∈ [0, 2π)
 *   radius    — per-point turn radius (defaults to the global turnRadiusMeters)
 *
 * The path between consecutive waypoints is a Dubins-like segment:
 *   exit arc (leaving point i along its direction, turning to aim at point i+1)
 *   straight segment
 *   entry arc (arriving at point i+1, turning into its direction)
 */

import type {
    DubinsCostMode,
    MapPath,
    MissionPointDraft,
    MissionPointItem,
    MissionRouteConfig,
    MissionRouteMode,
} from './missionModels';

/* ------------------------------------------------------------------ */
/*  Shared geometry primitives                                        */
/* ------------------------------------------------------------------ */

export type XYPoint = { x: number; y: number };

const EPSILON = 1e-9;
const TWO_PI = Math.PI * 2;
const MIN_SAMPLE_STEP = 0.25;
const MAX_SAMPLE_STEP = 5;
const INFEASIBLE_PENALTY = 1e6;
/** Arc sweep beyond this threshold attracts a proportional loop penalty */
const LOOP_ARC_THRESHOLD = Math.PI; // 180° — anything above is a loop
/** Penalty multiplier per radian of excess arc beyond the threshold */
const LOOP_PENALTY_PER_RAD = 500;
/** Max arc sweep for the final render — cosmetic clamp only (not used in optimizer) */
const MAX_RENDER_ARC = Math.PI * 1.25;
const DEFAULT_SWEEP_ANGLES = 24;
const DEFAULT_WINDOW_SIZE = 2;

/* ------------------------------------------------------------------ */
/*  Cost-mode penalties                                                */
/* ------------------------------------------------------------------ */

/** Curvature cost for a single arc: sweepAngle (higher = more turning). */
const arcCurvatureCost = (sweepAngle: number) => Math.abs(sweepAngle);

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
const roundN = (v: number, d = 2) => Number(v.toFixed(d));

const magnitude = (v: XYPoint) => Math.sqrt(v.x * v.x + v.y * v.y);
const subtract = (a: XYPoint, b: XYPoint): XYPoint => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: XYPoint, b: XYPoint): XYPoint => ({ x: a.x + b.x, y: a.y + b.y });
const scale = (v: XYPoint, f: number): XYPoint => ({ x: v.x * f, y: v.y * f });
const dot = (a: XYPoint, b: XYPoint) => a.x * b.x + a.y * b.y;
const cross2 = (a: XYPoint, b: XYPoint) => a.x * b.y - a.y * b.x;
const distXY = (a: XYPoint, b: XYPoint) => magnitude(subtract(a, b));
const norm = (v: XYPoint): XYPoint | null => {
    const l = magnitude(v);
    return l > EPSILON ? { x: v.x / l, y: v.y / l } : null;
};
const leftNormal = (v: XYPoint): XYPoint => ({ x: -v.y, y: v.x });

const angleFromDir = (dir: XYPoint) => Math.atan2(dir.y, dir.x);
const dirFromAngle = (a: number): XYPoint => ({ x: Math.cos(a), y: Math.sin(a) });

const normalizeAngle = (a: number) => {
    let n = a;
    while (n <= -Math.PI) n += TWO_PI;
    while (n > Math.PI) n -= TWO_PI;
    return n;
};

/* ------------------------------------------------------------------ */
/*  Lat/Lng ↔ XY projection                                          */
/* ------------------------------------------------------------------ */

type RouteControlPoint = {
    lat?: number | null;
    lng?: number | null;
    source?: 'pattern' | 'custom';
    patternPointId?: number | null;
    params?: Record<string, any> | null;
};

export type LocalRouteControlPoint = {
    x?: number | null;
    y?: number | null;
    source?: 'pattern' | 'custom';
    patternPointId?: number | null;
    params?: Record<string, any> | null;
};

export type LocalMapPath = {
    id: number | string;
    coordinates: XYPoint[];
    selected?: boolean;
    warning?: boolean;
};

const isValidCoord = (v: number | null | undefined): v is number => Number.isFinite(Number(v));

const isValidPoint = (p?: RouteControlPoint | null): p is Required<RouteControlPoint> =>
    Boolean(p && isValidCoord(p.lat) && isValidCoord(p.lng));

const isValidLocalPoint = (p?: LocalRouteControlPoint | null): p is Required<LocalRouteControlPoint> =>
    Boolean(p && isValidCoord(p.x) && isValidCoord(p.y));

const buildProjection = (points: RouteControlPoint[]) => {
    const valid = points.filter(isValidPoint);
    if (!valid.length) {
        return null;
    }
    const refLat = valid.reduce((s, p) => s + Number(p.lat), 0) / valid.length;
    const refLng = valid.reduce((s, p) => s + Number(p.lng), 0) / valid.length;
    const mPerDegLat = 111132.92;
    const mPerDegLng = 111319.49 * Math.cos((refLat * Math.PI) / 180);

    return {
        toXY: (p: Required<RouteControlPoint>): XYPoint => ({
            x: (Number(p.lng) - refLng) * mPerDegLng,
            y: (Number(p.lat) - refLat) * mPerDegLat,
        }),
        fromXY: (pt: XYPoint) => ({
            lat: pt.y / mPerDegLat + refLat,
            lng: pt.x / mPerDegLng + refLng,
        }),
    };
};

/* ------------------------------------------------------------------ */
/*  Dubins single-leg cost: exit arc + straight + entry arc            */
/* ------------------------------------------------------------------ */

type DubinsLegResult = {
    feasible: boolean;
    cost: number; // total arc + straight distance
    exitArcCenter: XYPoint | null;
    exitArcStart: XYPoint;
    exitArcEnd: XYPoint;
    exitRadius: number;
    exitOrientation: 1 | -1;
    entryArcCenter: XYPoint | null;
    entryArcStart: XYPoint;
    entryArcEnd: XYPoint;
    entryRadius: number;
    entryOrientation: 1 | -1;
    straightStart: XYPoint;
    straightEnd: XYPoint;
};

/**
 * Compute a CSC (curve-straight-curve) Dubins-like path between two oriented waypoints.
 *
 * Returns the cheaper of the four CSC variants (LSL, RSR, LSR, RSL).
 */
const computeDubinsLeg = (
    p1: XYPoint,
    dir1: XYPoint,
    r1: number,
    p2: XYPoint,
    dir2: XYPoint,
    r2: number
): DubinsLegResult => {
    const fallback: DubinsLegResult = {
        feasible: false,
        cost: INFEASIBLE_PENALTY,
        exitArcCenter: null,
        exitArcStart: p1,
        exitArcEnd: p1,
        exitRadius: r1,
        exitOrientation: 1,
        entryArcCenter: null,
        entryArcStart: p2,
        entryArcEnd: p2,
        entryRadius: r2,
        entryOrientation: 1,
        straightStart: p1,
        straightEnd: p2,
    };

    // If either radius is ~0 just do straight line
    if (r1 < 0.01 && r2 < 0.01) {
        return {
            ...fallback,
            feasible: true,
            cost: distXY(p1, p2),
            straightStart: p1,
            straightEnd: p2,
        };
    }

    let best: DubinsLegResult = fallback;

    // Try all four combinations: (L/R exit) × (L/R entry)
    const orientations: Array<[1 | -1, 1 | -1]> = [[1, 1], [-1, -1], [1, -1], [-1, 1]];

    for (const [exitOr, entryOr] of orientations) {
        const eR1 = Math.max(r1, 0.01);
        const eR2 = Math.max(r2, 0.01);

        // Exit circle center
        const exitNorm = scale(leftNormal(dir1), exitOr);
        const c1 = add(p1, scale(exitNorm, eR1));

        // Entry circle center
        const entryNorm = scale(leftNormal(dir2), entryOr);
        const c2 = add(p2, scale(entryNorm, eR2));

        const d = distXY(c1, c2);
        if (d < EPSILON) {
            continue;
        }

        // Compute tangent line between the two circles
        let tangentAngle: number;
        const baseAngle = Math.atan2(c2.y - c1.y, c2.x - c1.x);

        if (exitOr === entryOr) {
            // Same-side: outer tangent (LSL or RSR)
            const deltaR = eR2 - eR1;
            if (d < Math.abs(deltaR) - EPSILON) {
                continue; // one circle inside the other
            }
            const sinA = deltaR / d;
            const cosA = Math.sqrt(Math.max(0, 1 - sinA * sinA));
            if (!Number.isFinite(cosA)) {
                continue;
            }
            tangentAngle = baseAngle + (exitOr > 0 ? Math.atan2(-sinA, cosA) : Math.atan2(sinA, cosA));
        } else {
            // Cross-side: inner tangent (LSR or RSL)
            const sumR = eR1 + eR2;
            if (d < sumR - EPSILON) {
                continue; // circles overlap
            }
            const sinA = sumR / d;
            if (sinA > 1 + EPSILON) {
                continue;
            }
            const cosA = Math.sqrt(Math.max(0, 1 - clamp(sinA, -1, 1) ** 2));
            tangentAngle = baseAngle + (exitOr > 0 ? Math.atan2(sinA, cosA) : Math.atan2(-sinA, cosA));
        }

        // Tangent direction
        const tangentDir = dirFromAngle(tangentAngle);

        // Tangent points on each circle
        const tangentNormal = leftNormal(tangentDir);
        const tp1 = add(c1, scale(tangentNormal, -exitOr * eR1));
        const tp2 = add(c2, scale(tangentNormal, -entryOr * eR2));

        // Arc lengths
        const exitArcDelta = computeArcDeltaSigned(c1, p1, tp1, exitOr);
        const entryArcDelta = computeArcDeltaSigned(c2, tp2, p2, entryOr);

        const exitArcLen = Math.abs(exitArcDelta) * eR1;
        const entryArcLen = Math.abs(entryArcDelta) * eR2;
        const straightLen = distXY(tp1, tp2);

        // Soft loop penalty: proportional to excess arc beyond threshold
        let loopPenalty = 0;
        const exitExcess = Math.abs(exitArcDelta) - LOOP_ARC_THRESHOLD;
        const entryExcess = Math.abs(entryArcDelta) - LOOP_ARC_THRESHOLD;
        if (exitExcess > 0) loopPenalty += exitExcess * LOOP_PENALTY_PER_RAD * eR1;
        if (entryExcess > 0) loopPenalty += entryExcess * LOOP_PENALTY_PER_RAD * eR2;

        const totalCost = exitArcLen + straightLen + entryArcLen + loopPenalty;

        if (totalCost < best.cost) {
            best = {
                feasible: true,
                cost: totalCost,
                exitArcCenter: c1,
                exitArcStart: p1,
                exitArcEnd: tp1,
                exitRadius: eR1,
                exitOrientation: exitOr,
                entryArcCenter: c2,
                entryArcStart: tp2,
                entryArcEnd: p2,
                entryRadius: eR2,
                entryOrientation: entryOr,
                straightStart: tp1,
                straightEnd: tp2,
            };
        }
    }

    return best;
};

const computeArcDeltaSigned = (
    center: XYPoint,
    start: XYPoint,
    end: XYPoint,
    orientation: 1 | -1
) => {
    const sA = Math.atan2(start.y - center.y, start.x - center.x);
    const eA = Math.atan2(end.y - center.y, end.x - center.x);
    let delta = normalizeAngle(eA - sA);

    if (orientation > 0 && delta < 0) delta += TWO_PI;
    else if (orientation < 0 && delta > 0) delta -= TWO_PI;

    return delta;
};

/* ------------------------------------------------------------------ */
/*  Path sampling                                                      */
/* ------------------------------------------------------------------ */

const sampleArc = (
    center: XYPoint,
    start: XYPoint,
    end: XYPoint,
    radius: number,
    orientation: 1 | -1,
    step: number
): XYPoint[] => {
    if (!center) return [start, end];
    const delta = computeArcDeltaSigned(center, start, end, orientation);
    const arcLen = Math.abs(delta) * radius;
    if (arcLen < EPSILON) return [start];
    const steps = Math.max(2, Math.ceil(arcLen / Math.max(step, MIN_SAMPLE_STEP)));
    const sA = Math.atan2(start.y - center.y, start.x - center.x);
    const pts: XYPoint[] = [];
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const a = sA + delta * t;
        pts.push({ x: center.x + Math.cos(a) * radius, y: center.y + Math.sin(a) * radius });
    }
    return pts;
};

const sampleStraight = (a: XYPoint, b: XYPoint, step: number): XYPoint[] => {
    const d = distXY(a, b);
    if (d < EPSILON) return [a];
    const n = Math.max(1, Math.ceil(d / Math.max(step, MIN_SAMPLE_STEP)));
    const pts: XYPoint[] = [];
    for (let i = 0; i <= n; i++) {
        const t = i / n;
        pts.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
    return pts;
};

const pushUnique = (arr: XYPoint[], pt: XYPoint) => {
    const last = arr[arr.length - 1];
    if (!last || distXY(last, pt) > 0.001) {
        arr.push(pt);
    } else {
        arr[arr.length - 1] = pt;
    }
};

const appendPoly = (base: XYPoint[], pts: XYPoint[]) => {
    pts.forEach((p) => pushUnique(base, p));
};

/* ------------------------------------------------------------------ */
/*  Seed directions: angle bisector of consecutive segments            */
/* ------------------------------------------------------------------ */

const seedDirections = (xyPoints: XYPoint[]): number[] => {
    const n = xyPoints.length;
    const dirs: number[] = new Array(n);

    if (n === 0) return dirs;
    if (n === 1) {
        dirs[0] = 0;
        return dirs;
    }

    // First point: direction toward second point
    const first = norm(subtract(xyPoints[1], xyPoints[0]));
    dirs[0] = first ? angleFromDir(first) : 0;

    // Interior points: bisector of incoming/outgoing segments
    for (let i = 1; i < n - 1; i++) {
        const incoming = norm(subtract(xyPoints[i], xyPoints[i - 1]));
        const outgoing = norm(subtract(xyPoints[i + 1], xyPoints[i]));
        if (incoming && outgoing) {
            // Average the two unit vectors for the bisector
            const bisector = norm(add(incoming, outgoing));
            dirs[i] = bisector ? angleFromDir(bisector) : angleFromDir(incoming);
        } else if (incoming) {
            dirs[i] = angleFromDir(incoming);
        } else if (outgoing) {
            dirs[i] = angleFromDir(outgoing);
        } else {
            dirs[i] = 0;
        }
    }

    // Last point: direction from second-to-last point
    const last = norm(subtract(xyPoints[n - 1], xyPoints[n - 2]));
    dirs[n - 1] = last ? angleFromDir(last) : 0;

    return dirs;
};

/* ------------------------------------------------------------------ */
/*  Total route cost given a set of directions and radii               */
/* ------------------------------------------------------------------ */

type CostMode = DubinsCostMode;

type CostResult = {
    cost: number;
    /** Point indices involved in legs that have looping arcs (sweep > π) */
    loopingPoints: number[];
};

const computeTotalCostWithLoopInfo = (
    xyPoints: XYPoint[],
    directions: number[],
    radii: number[],
    costMode: CostMode = 'shortest'
): CostResult => {
    let totalDistance = 0;
    let totalCurvature = 0;
    const loopingPointSet = new Set<number>();

    for (let i = 0; i < xyPoints.length - 1; i++) {
        const leg = computeDubinsLeg(
            xyPoints[i],
            dirFromAngle(directions[i]),
            radii[i],
            xyPoints[i + 1],
            dirFromAngle(directions[i + 1]),
            radii[i + 1]
        );
        totalDistance += leg.cost;

        // Compute curvature penalty from arc sweep angles
        if (leg.feasible && leg.exitArcCenter) {
            const exitDelta = computeArcDeltaSigned(leg.exitArcCenter, leg.exitArcStart, leg.exitArcEnd, leg.exitOrientation);
            totalCurvature += arcCurvatureCost(exitDelta);
            if (Math.abs(exitDelta) > LOOP_ARC_THRESHOLD) {
                loopingPointSet.add(i);     // exit point of this leg
            }
        }
        if (leg.feasible && leg.entryArcCenter) {
            const entryDelta = computeArcDeltaSigned(leg.entryArcCenter, leg.entryArcStart, leg.entryArcEnd, leg.entryOrientation);
            totalCurvature += arcCurvatureCost(entryDelta);
            if (Math.abs(entryDelta) > LOOP_ARC_THRESHOLD) {
                loopingPointSet.add(i + 1); // entry point of this leg
            }
        }
    }

    let cost: number;
    switch (costMode) {
        case 'smoothest':
            cost = totalDistance + 3.0 * totalCurvature * totalCurvature;
            break;
        case 'balanced':
            cost = totalDistance + 1.5 * totalCurvature;
            break;
        case 'shortest':
        default:
            cost = totalDistance;
    }
    return { cost, loopingPoints: Array.from(loopingPointSet) };
};

/** Convenience wrapper that returns just the cost number */
const computeTotalCost = (
    xyPoints: XYPoint[],
    directions: number[],
    radii: number[],
    costMode: CostMode = 'shortest'
): number => computeTotalCostWithLoopInfo(xyPoints, directions, radii, costMode).cost;

/* ------------------------------------------------------------------ */
/*  Seeded PRNG (Mulberry32) for deterministic Monte Carlo             */
/* ------------------------------------------------------------------ */

const mulberry32 = (seed: number) => {
    let s = seed | 0;
    return () => {
        s = (s + 0x6D2B79F5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

const hashXYPoints = (points: XYPoint[]): number => {
    let h = 0x811c9dc5; // FNV offset basis
    for (let i = 0; i < points.length; i++) {
        // Quantise to ~0.1mm to avoid floating-point noise
        const xi = Math.round(points[i].x * 10000);
        const yi = Math.round(points[i].y * 10000);
        h = Math.imul(h ^ xi, 0x01000193);
        h = Math.imul(h ^ yi, 0x01000193);
    }
    return h >>> 0;
};

/* ------------------------------------------------------------------ */
/*  Monte Carlo optimizer (seeded — deterministic for same points)     */
/* ------------------------------------------------------------------ */

type OptimizeOptions = {
    costMode?: CostMode;
    sweepAngles?: number;
    windowSize?: number;
};

/**
 * Greedy sequential optimizer.
 *
 * Processes points left-to-right in a sliding window.  For each new point
 * entering the window, we sweep `sweepAngles` evenly-spaced headings and
 * keep the one that minimises the cost of all legs inside the window.
 *
 * windowSize = 2 (default): pure pair-by-pair — P1→P2, then P2→P3, …
 * windowSize = 3: evaluates 2 legs at a time for more global context
 * windowSize = N: evaluates all legs (full global, most expensive)
 */
const optimizeDirections = (
    xyPoints: XYPoint[],
    initialDirections: number[],
    initialRadii: number[],
    locked: boolean[],
    options: OptimizeOptions = {}
): { directions: number[]; radii: number[] } => {
    const {
        costMode = 'shortest',
        sweepAngles = DEFAULT_SWEEP_ANGLES,
        windowSize = DEFAULT_WINDOW_SIZE,
    } = options;

    const n = xyPoints.length;
    if (n < 2) {
        return { directions: [...initialDirections], radii: [...initialRadii] };
    }

    const dirs = [...initialDirections];
    const radii = [...initialRadii];
    const nSweep = clamp(Math.round(sweepAngles), 10, 100);
    const wSize = clamp(Math.round(windowSize), 2, n);

    // Cost of a sub-path from index `from` to index `to` (inclusive)
    const windowCost = (from: number, to: number): number => {
        let totalDistance = 0;
        let totalCurvature = 0;
        for (let i = from; i < to; i++) {
            const leg = computeDubinsLeg(
                xyPoints[i], dirFromAngle(dirs[i]), radii[i],
                xyPoints[i + 1], dirFromAngle(dirs[i + 1]), radii[i + 1]
            );
            totalDistance += leg.cost;
            if (leg.feasible && leg.exitArcCenter) {
                totalCurvature += arcCurvatureCost(
                    computeArcDeltaSigned(leg.exitArcCenter, leg.exitArcStart, leg.exitArcEnd, leg.exitOrientation)
                );
            }
            if (leg.feasible && leg.entryArcCenter) {
                totalCurvature += arcCurvatureCost(
                    computeArcDeltaSigned(leg.entryArcCenter, leg.entryArcStart, leg.entryArcEnd, leg.entryOrientation)
                );
            }
        }
        switch (costMode) {
            case 'smoothest': return totalDistance + 3.0 * totalCurvature * totalCurvature;
            case 'balanced': return totalDistance + 1.5 * totalCurvature;
            case 'shortest': default: return totalDistance;
        }
    };

    // --- Forward pass: sweep each free point's heading sequentially ---
    for (let i = 1; i < n; i++) {
        if (locked[i]) continue; // respect locked headings

        // Window spans from max(0, i - wSize + 1) to min(n-1, i)
        const wStart = Math.max(0, i - wSize + 1);
        const wEnd = Math.min(n - 1, i); // last point index in window

        let bestAngle = dirs[i];
        let bestWindowCost = windowCost(wStart, wEnd);

        for (let s = 0; s < nSweep; s++) {
            const candidate = -Math.PI + (s / nSweep) * TWO_PI;
            dirs[i] = candidate;
            const c = windowCost(wStart, wEnd);
            if (c < bestWindowCost) {
                bestWindowCost = c;
                bestAngle = candidate;
            }
        }

        dirs[i] = bestAngle;
    }

    return { directions: dirs, radii };
};

/* ------------------------------------------------------------------ */
/*  Route config normalization (mirrors the original)                  */
/* ------------------------------------------------------------------ */

const DEFAULT_TURN_RADIUS = 3;

export const normalizeDubinsRouteConfig = (value?: Partial<MissionRouteConfig> | null): MissionRouteConfig => {
    const c = value && typeof value === 'object' ? value : {};
    const turnRadiusMeters = clamp(
        Number.isFinite(Number(c.turnRadiusMeters)) ? Number(c.turnRadiusMeters) : DEFAULT_TURN_RADIUS,
        0,
        100
    );
    const sampleStepMeters = clamp(
        Number.isFinite(Number(c.sampleStepMeters))
            ? Number(c.sampleStepMeters)
            : turnRadiusMeters > 0
                ? clamp(turnRadiusMeters / 4, 0.75, 2.5)
                : 2,
        MIN_SAMPLE_STEP,
        MAX_SAMPLE_STEP
    );

    return {
        turnRadiusMeters: roundN(turnRadiusMeters, 2),
        sampleStepMeters: roundN(sampleStepMeters, 2),
        routingMode: 'dubins' as MissionRouteMode,
    };
};

/* ------------------------------------------------------------------ */
/*  Extract coordinates from draft / item points                       */
/* ------------------------------------------------------------------ */

const getPointCoordinates = (point: MissionPointDraft | MissionPointItem): RouteControlPoint => {
    const c = point as MissionPointItem & MissionPointDraft;
    return {
        lat: c.resolvedLocation?.lat ?? c.lat ?? null,
        lng: c.resolvedLocation?.lng ?? c.lng ?? null,
        source: c.source || (c.patternPointId ? 'pattern' : 'custom'),
        patternPointId: c.patternPointId ?? null,
        params: c.params || {},
    };
};

/* ------------------------------------------------------------------ */
/*  Public: build preview paths using Dubins optimizer                  */
/* ------------------------------------------------------------------ */

export type DubinsOptimizerOptions = {
    costMode?: DubinsCostMode;
    sweepAngles?: number;
    windowSize?: number;
};

const buildDubinsXYPaths = <TCoordinate>(
    controlPoints: LocalRouteControlPoint[],
    routeConfigValue: Partial<MissionRouteConfig> | null | undefined,
    optimizerOptions: DubinsOptimizerOptions | undefined,
    mapCoordinate: (point: XYPoint) => TCoordinate,
    idPrefix: string
): Array<{
    id: number | string;
    coordinates: TCoordinate[];
    selected?: boolean;
    warning?: boolean;
}> => {
    const routeConfig = normalizeDubinsRouteConfig(routeConfigValue);
    const costMode: CostMode = optimizerOptions?.costMode || (routeConfigValue as any)?.costMode || 'shortest';
    const sweepAngles = optimizerOptions?.sweepAngles ?? (routeConfigValue as any)?.sweepAngles ?? DEFAULT_SWEEP_ANGLES;
    const windowSize = optimizerOptions?.windowSize ?? (routeConfigValue as any)?.windowSize ?? DEFAULT_WINDOW_SIZE;

    if (controlPoints.filter(isValidLocalPoint).length < 2) {
        return [];
    }

    type IndexedXY = { actualIndex: number; xy: XYPoint };
    const blocks: IndexedXY[][] = [];
    let currentBlock: IndexedXY[] = [];

    controlPoints.forEach((cp, idx) => {
        if (isValidLocalPoint(cp)) {
            currentBlock.push({ actualIndex: idx, xy: { x: Number(cp.x), y: Number(cp.y) } });
        } else if (currentBlock.length > 0) {
            blocks.push(currentBlock);
            currentBlock = [];
        }
    });
    if (currentBlock.length > 0) {
        blocks.push(currentBlock);
    }

    const legPaths: Array<{
        id: number | string;
        coordinates: TCoordinate[];
        selected?: boolean;
        warning?: boolean;
    }> = [];

    blocks.forEach((block) => {
        if (block.length < 2) return;

        const xyPoints = block.map((b) => b.xy);
        const n = xyPoints.length;

        const initialDirs = seedDirections(xyPoints);
        const initialRadii = new Array(n).fill(routeConfig.turnRadiusMeters);
        const lockedFlags = new Array(n).fill(false);

        block.forEach((b, i) => {
            const params = controlPoints[b.actualIndex]?.params;
            const dirMode = String(params?.pathDirectionMode || '').trim().toLowerCase();

            if ((dirMode === 'manual' || dirMode === 'pattern') && params?.pathDirection) {
                const dx = Number(params.pathDirection.x);
                const dy = Number(params.pathDirection.y);
                if (Number.isFinite(dx) && Number.isFinite(dy)) {
                    const v = norm({ x: dx, y: dy });
                    if (v) {
                        initialDirs[i] = angleFromDir(v);
                        lockedFlags[i] = true;
                    }
                }
            }

            const perPointRadius = Number(params?.pathTurnRadius);
            if (Number.isFinite(perPointRadius) && perPointRadius >= 0) {
                initialRadii[i] = clamp(perPointRadius, 0, 100);
            }
        });

        const { directions, radii } = optimizeDirections(
            xyPoints,
            initialDirs,
            initialRadii,
            lockedFlags,
            {
                costMode,
                sweepAngles,
                windowSize,
            }
        );

        for (let i = 0; i < n - 1; i++) {
            const leg = computeDubinsLeg(
                xyPoints[i],
                dirFromAngle(directions[i]),
                radii[i],
                xyPoints[i + 1],
                dirFromAngle(directions[i + 1]),
                radii[i + 1]
            );

            const polyline: XYPoint[] = [];

            if (leg.feasible && leg.exitArcCenter) {
                appendPoly(
                    polyline,
                    sampleArc(
                        leg.exitArcCenter,
                        leg.exitArcStart,
                        leg.exitArcEnd,
                        leg.exitRadius,
                        leg.exitOrientation,
                        routeConfig.sampleStepMeters
                    )
                );
            }

            appendPoly(polyline, sampleStraight(leg.straightStart, leg.straightEnd, routeConfig.sampleStepMeters));

            if (leg.feasible && leg.entryArcCenter) {
                appendPoly(
                    polyline,
                    sampleArc(
                        leg.entryArcCenter,
                        leg.entryArcStart,
                        leg.entryArcEnd,
                        leg.entryRadius,
                        leg.entryOrientation,
                        routeConfig.sampleStepMeters
                    )
                );
            }

            if (polyline.length >= 2) {
                legPaths.push({
                    id: `${idPrefix}-${block[0].actualIndex}-${i}`,
                    selected: true,
                    coordinates: polyline.map(mapCoordinate),
                    warning: !leg.feasible,
                });
            }
        }
    });

    return legPaths;
};

export const buildMissionPreviewPathsDubinsFromXY = (
    controlPoints: LocalRouteControlPoint[],
    routeConfigValue?: Partial<MissionRouteConfig> | null,
    optimizerOptions?: DubinsOptimizerOptions
): LocalMapPath[] => buildDubinsXYPaths(
    controlPoints,
    routeConfigValue,
    optimizerOptions,
    (point) => point,
    'local-dubins-path'
);

export const buildMissionPreviewPathsDubins = (
    points: Array<MissionPointDraft | MissionPointItem>,
    routeConfigValue?: Partial<MissionRouteConfig> | null,
    optimizerOptions?: DubinsOptimizerOptions
): MapPath[] => {
    const controlPoints = points.map(getPointCoordinates);
    const validCPs = controlPoints.filter(isValidPoint);

    if (validCPs.length < 2) {
        return [];
    }

    const proj = buildProjection(controlPoints);
    if (!proj) {
        return [];
    }

    const xyControlPoints: LocalRouteControlPoint[] = controlPoints.map((point) => {
        if (!isValidPoint(point)) {
            return {
                x: null,
                y: null,
                source: point.source,
                patternPointId: point.patternPointId,
                params: point.params,
            };
        }
        const xy = proj.toXY(point);
        return {
            x: xy.x,
            y: xy.y,
            source: point.source,
            patternPointId: point.patternPointId,
            params: point.params,
        };
    });

    return buildDubinsXYPaths(
        xyControlPoints,
        routeConfigValue,
        optimizerOptions,
        proj.fromXY,
        'dubins-path'
    );
};
